import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { MetricChart } from '../shared/charts/MetricChart';
import { MetricChartConfig, ChartDataPoint, ChartDatasetConfig } from '../shared/charts/ChartTypes';
import { NutritionDaySummary } from '../../hooks/useNutritionSummary';
import { GoalTarget } from '../client/HealthCalendarTile';
import { useTheme } from '../../contexts/ThemeContext';
import { getMetricComplianceColor } from '../../utils/goalCompliance';

/**
 * NUTRITION GRAPH - Phase 9e
 *
 * Line chart with metric selector dropdown.
 * Uses the existing modular chart system (MetricChart).
 * Mirrors FatSecret's Graph_Menu.png layout.
 */

interface NutritionGraphProps {
  days: NutritionDaySummary[];
  highlightedDate?: string | null;
  onDateSelect?: (date: string) => void;
  goalsData?: Map<string, Record<string, GoalTarget>>;
}

// Metric options matching FatSecret dropdown
const metricOptions = [
  { key: 'calories', label: 'Calories', unit: '', color: '#10b981' },
  { key: 'fats_g', label: 'Fat', unit: 'g', color: '#f59e0b' },
  { key: 'saturated_fat_g', label: 'Saturated Fat', unit: 'g', color: '#ef4444' },
  { key: 'polyunsaturated_fat_g', label: 'Polyunsaturated Fat', unit: 'g', color: '#8b5cf6' },
  { key: 'monounsaturated_fat_g', label: 'Monounsaturated Fat', unit: 'g', color: '#ec4899' },
  { key: 'carbs_g', label: 'Carbohydrate', unit: 'g', color: '#3b82f6' },
  { key: 'fiber_g', label: 'Fiber', unit: 'g', color: '#84cc16' },
  { key: 'sugar_g', label: 'Sugar', unit: 'g', color: '#f97316' },
  { key: 'protein_g', label: 'Protein', unit: 'g', color: '#06b6d4' },
  { key: 'cholesterol_mg', label: 'Cholesterol', unit: 'mg', color: '#a855f7' },
  { key: 'sodium_mg', label: 'Sodium', unit: 'mg', color: '#64748b' },
  { key: 'potassium_mg', label: 'Potassium', unit: 'mg', color: '#14b8a6' },
];

// Format date for x-axis label: "12/25" (compact format)
const formatDateLabel = (dateStr: string): string => {
  const date = new Date(dateStr + 'T00:00:00');
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${month}/${day}`;
};

export const NutritionGraph: React.FC<NutritionGraphProps> = ({ days, highlightedDate, onDateSelect, goalsData }) => {
  useTheme(); // Subscribe to theme changes for goal line color re-render
  const [selectedMetric, setSelectedMetric] = useState('calories');
  const goalLineColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--goal-line-color').trim() || '#ffffff';

  // Zoom state: 0 = fully zoomed out (default), 1 = fully zoomed in
  const [zoomLevel, setZoomLevel] = useState(0);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // Reset zoom when metric changes
  const handleMetricChange = useCallback((metric: string) => {
    setSelectedMetric(metric);
    setZoomLevel(0);
  }, []);

  // Get current metric config
  const metricConfig = useMemo(
    () => metricOptions.find((m) => m.key === selectedMetric) || metricOptions[0],
    [selectedMetric]
  );

  // Build chart configuration using the modular chart system
  const chartConfig: MetricChartConfig = useMemo(() => {
    // Sort all days by date (keep zero-data days to show gaps)
    const sortedDays = [...days].sort((a, b) => a.date.localeCompare(b.date));

    // Transform to ChartDataPoint format
    // Days with zero calories show as 0 (line drops to baseline)
    const dataPoints: ChartDataPoint[] = sortedDays.map((day) => {
      const hasData = day.calories > 0;
      return {
        label: formatDateLabel(day.date),
        value: hasData ? ((day as any)[selectedMetric] || 0) : 0,
        metadata: {
          date: day.date,
          hasData,
          fullDate: new Date(day.date + 'T00:00:00').toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })
        }
      };
    });

    // Check if the selected metric has any active goals in the visible range
    const hasGoalForMetric = goalsData && sortedDays.some(day => {
      const goals = goalsData.get(day.date);
      return goals?.[selectedMetric] != null;
    });

    // Neutral line color when compliance coloring is active
    const neutralLineColor = '#6b7fad';
    const lineColor = hasGoalForMetric ? neutralLineColor : metricConfig.color;

    // Build per-point colors based on compliance (or metric default)
    const highlightIndex = highlightedDate
      ? sortedDays.findIndex(d => d.date === highlightedDate)
      : -1;

    const pointColors = sortedDays.map((day, i) => {
      // Highlighted point is always white
      if (i === highlightIndex) return '#ffffff';

      // Compliance coloring when goals exist for this metric
      if (hasGoalForMetric && day.calories > 0) {
        const goals = goalsData!.get(day.date);
        const goal = goals?.[selectedMetric];
        const value = (day as any)[selectedMetric] || 0;
        const color = getMetricComplianceColor(value, goal);
        if (color) return color;
      }

      return lineColor;
    });

    const pointRadii = highlightIndex >= 0
      ? dataPoints.map((_, i) => i === highlightIndex ? 10 : 6)
      : undefined;

    // Build goal boundary datasets from per-day targets_at_time
    const goalDatasets: ChartDatasetConfig[] = [];
    if (goalsData) {
      // Collect upper/lower boundary values per day for the selected metric
      const upperValues: (number | null)[] = [];
      const lowerValues: (number | null)[] = [];
      let hasUpper = false;
      let hasLower = false;

      sortedDays.forEach((day) => {
        const goals = goalsData.get(day.date);
        const goal = goals?.[selectedMetric];
        if (!goal) {
          upperValues.push(null);
          lowerValues.push(null);
          return;
        }

        switch (goal.goal_type) {
          case 'range':
            upperValues.push(goal.goal_value);  // ceiling
            lowerValues.push(goal.floor_value ?? null);
            hasUpper = true;
            if (goal.floor_value != null) hasLower = true;
            break;
          case 'target': {
            const variance = (goal.variance_percent || 5) / 100;
            upperValues.push(goal.goal_value * (1 + variance));
            lowerValues.push(goal.goal_value * (1 - variance));
            hasUpper = true;
            hasLower = true;
            break;
          }
          case 'over':
            upperValues.push(goal.goal_value);
            lowerValues.push(null);
            hasUpper = true;
            break;
          case 'under':
            upperValues.push(goal.goal_value);
            lowerValues.push(null);
            hasUpper = true;
            break;
          default:
            upperValues.push(null);
            lowerValues.push(null);
        }
      });

      const goalLineStyle = {
        showPoints: false,
        fill: false,
        tension: 0,
        borderDash: [6, 4],
        borderWidth: 1.5,
        color: goalLineColor,
        spanGaps: false,
        order: 0,  // Draw on top of the filled area
      };

      if (hasUpper) {
        goalDatasets.push({
          ...goalLineStyle,
          label: '_upper_goal',
          hidden: true,
          data: dataPoints.map((dp, i) => ({
            label: dp.label,
            value: upperValues[i],
            metadata: dp.metadata,
          })),
        });
      }
      if (hasLower) {
        goalDatasets.push({
          ...goalLineStyle,
          label: '_lower_goal',
          hidden: true,
          data: dataPoints.map((dp, i) => ({
            label: dp.label,
            value: lowerValues[i],
            metadata: dp.metadata,
          })),
        });
      }
    }

    return {
      type: 'area',
      datasets: [{
        label: metricConfig.label,
        data: dataPoints,
        color: lineColor,
        showPoints: true,
        fill: true,
        tension: 0.3,
        order: 1,  // Draw under goal lines
        pointColors,
        ...(pointRadii ? { pointRadii } : {}),
      }, ...goalDatasets],
      colors: {
        primary: lineColor,
      },
      height: 220,
      responsive: true,
      maintainAspectRatio: false,
      yAxisBeginAtZero: false,
      yAxisLabel: metricConfig.unit || undefined,
      showLegend: false,
      showTooltips: true,
      customTooltip: (dataPoint) => {
        if (!dataPoint.metadata?.hasData) {
          return [
            dataPoint.metadata?.fullDate || dataPoint.label,
            'No data logged'
          ];
        }
        const unit = metricConfig.unit ? ` ${metricConfig.unit}` : '';
        return [
          dataPoint.metadata?.fullDate || dataPoint.label,
          `${metricConfig.label}: ${Math.round(dataPoint.value!)}${unit}`
        ];
      },
      emptyMessage: 'No nutrition data available for graph',
      onPointClick: onDateSelect
        ? (dataPoint) => {
            const date = dataPoint.metadata?.date;
            if (!date || !dataPoint.metadata?.hasData) {
              onDateSelect('');
            } else if (date === highlightedDate) {
              onDateSelect('');
            } else {
              onDateSelect(date);
            }
          }
        : undefined,
      onBackgroundClick: onDateSelect ? () => onDateSelect('') : undefined,
    };
  }, [days, selectedMetric, metricConfig, highlightedDate, onDateSelect, goalsData, goalLineColor]);

  // Calculate stats for display
  const stats = useMemo(() => {
    const daysWithData = days.filter((d) => d.calories > 0);
    if (daysWithData.length === 0) return null;

    const values = daysWithData.map((d) => (d as any)[selectedMetric] || 0);
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    const max = Math.max(...values);
    const min = Math.min(...values);

    return { avg: Math.round(avg), max: Math.round(max), min: Math.round(min) };
  }, [days, selectedMetric]);

  // Compute zoom bounds: interpolate between fully-out and fully-in Y-axis range
  const zoomBounds = useMemo(() => {
    // Collect all data values + goal boundary values to find the full range
    const allValues: number[] = [];
    const goalBoundaries: number[] = [];

    const daysWithData = days.filter(d => d.calories > 0);
    daysWithData.forEach(d => {
      const v = (d as any)[selectedMetric] || 0;
      if (v > 0) allValues.push(v);
    });

    // Collect goal boundaries for the selected metric
    if (goalsData) {
      days.forEach(day => {
        const goals = goalsData.get(day.date);
        const goal = goals?.[selectedMetric];
        if (!goal) return;
        switch (goal.goal_type) {
          case 'range':
            goalBoundaries.push(goal.goal_value);
            if (goal.floor_value != null) goalBoundaries.push(goal.floor_value);
            break;
          case 'target': {
            const v = (goal.variance_percent || 5) / 100;
            goalBoundaries.push(goal.goal_value * (1 + v));
            goalBoundaries.push(goal.goal_value * (1 - v));
            break;
          }
          case 'over':
          case 'under':
            goalBoundaries.push(goal.goal_value);
            break;
        }
      });
    }

    if (allValues.length === 0) return null;

    // Fully zoomed OUT: data + goals + 10% padding (matches chartUtils auto-calc)
    const allCombined = [...allValues, ...goalBoundaries];
    const fullMin = Math.min(...allCombined);
    const fullMax = Math.max(...allCombined);
    const fullRange = fullMax - fullMin;
    const fullPadding = fullRange * 0.1 || 10;
    const outMin = Math.max(0, fullMin - fullPadding);
    const outMax = fullMax + fullPadding;

    // Fully zoomed IN: centered on goal boundaries with ±10% padding
    // Data points outside this range will clip off — that's the point of zooming
    let inMin: number, inMax: number;
    if (goalBoundaries.length > 0) {
      const goalMin = Math.min(...goalBoundaries);
      const goalMax = Math.max(...goalBoundaries);
      const goalRange = goalMax - goalMin || goalMax * 0.1;
      const goalCenter = (goalMin + goalMax) / 2;
      const halfSpan = goalRange * 0.6; // 10% padding on each side of the range
      inMin = Math.max(0, goalCenter - halfSpan);
      inMax = goalCenter + halfSpan;
    } else {
      // No goals — zoom into average ± tight range
      const dataMin = Math.min(...allValues);
      const dataMax = Math.max(...allValues);
      const dataCenter = (dataMin + dataMax) / 2;
      const halfSpan = (dataMax - dataMin) * 0.3 || 10;
      inMin = Math.max(0, dataCenter - halfSpan);
      inMax = dataCenter + halfSpan;
    }

    return { outMin, outMax, inMin, inMax };
  }, [days, selectedMetric, goalsData]);

  // Apply zoom to chart config
  const zoomedConfig = useMemo(() => {
    if (!zoomBounds || zoomLevel === 0) return chartConfig;

    // Linear interpolation between out and in bounds
    const yMin = zoomBounds.outMin + (zoomBounds.inMin - zoomBounds.outMin) * zoomLevel;
    const yMax = zoomBounds.outMax + (zoomBounds.inMax - zoomBounds.outMax) * zoomLevel;

    return { ...chartConfig, yAxisMin: yMin, yAxisMax: yMax };
  }, [chartConfig, zoomLevel, zoomBounds]);

  // Attach native wheel listener with { passive: false } to allow preventDefault
  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const step = 0.08; // 8% per scroll tick
      setZoomLevel(prev => {
        const next = e.deltaY < 0
          ? Math.min(1, prev + step)   // scroll up = zoom in
          : Math.max(0, prev - step);  // scroll down = zoom out
        return next;
      });
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  // Click on non-chart areas (header, stats, padding) should also deselect
  const handleContainerClick = (e: React.MouseEvent) => {
    // Only deselect if clicking directly on the container or header areas, not the chart canvas
    const target = e.target as HTMLElement;
    if (target.tagName === 'CANVAS') return; // Let chart onClick handle it
    if (target.closest('.metric-selector')) return; // Don't deselect when using dropdown
    if (highlightedDate && onDateSelect) onDateSelect('');
  };

  return (
    <div className="nutrition-graph" onClick={handleContainerClick}>
      <div className="graph-header">
        <div className="metric-selector">
          <select
            value={selectedMetric}
            onChange={(e) => handleMetricChange(e.target.value)}
            className="metric-dropdown"
          >
            {metricOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {stats && (
          <div className="graph-stats">
            <span className="stat">
              <span className="stat-label">Avg:</span>
              <span className="stat-value">{stats.avg.toLocaleString()}</span>
            </span>
            <span className="stat">
              <span className="stat-label">Min:</span>
              <span className="stat-value">{stats.min.toLocaleString()}</span>
            </span>
            <span className="stat">
              <span className="stat-label">Max:</span>
              <span className="stat-value">{stats.max.toLocaleString()}</span>
            </span>
          </div>
        )}
      </div>

      <div className="chart-container" ref={chartContainerRef}>
        <MetricChart config={zoomedConfig} />
        {zoomLevel > 0 && (
          <div className="zoom-indicator" title="Scroll down to zoom out, or click to reset">
            <button className="zoom-reset" onClick={() => setZoomLevel(0)}>
              {Math.round(zoomLevel * 100)}% zoom — click to reset
            </button>
          </div>
        )}
      </div>

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .nutrition-graph {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--bg-secondary);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-medium);
    padding: 16px;
  }

  .graph-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    flex-wrap: wrap;
    gap: 12px;
  }

  .metric-selector {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .metric-dropdown {
    padding: 8px 12px;
    font-size: 14px;
    font-weight: 500;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-medium);
    color: var(--text-primary);
    cursor: pointer;
    min-width: 180px;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    padding-right: 32px;
  }

  .metric-dropdown:hover {
    border-color: var(--accent-green);
  }

  .metric-dropdown:focus {
    outline: none;
    border-color: var(--accent-green);
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
  }

  .metric-dropdown option {
    background: var(--bg-secondary);
    color: var(--text-primary);
    padding: 8px;
  }

  .graph-stats {
    display: flex;
    gap: 16px;
  }

  .stat {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
  }

  .stat-label {
    color: var(--text-tertiary);
    font-weight: 500;
  }

  .stat-value {
    color: var(--text-primary);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .chart-container {
    flex: 1;
    min-height: 150px;
    position: relative;
  }

  .zoom-indicator {
    position: absolute;
    bottom: 4px;
    right: 8px;
    z-index: 5;
  }

  .zoom-reset {
    background: rgba(0, 0, 0, 0.5);
    color: #ccc;
    border: none;
    border-radius: 4px;
    padding: 2px 8px;
    font-size: 10px;
    cursor: pointer;
    opacity: 0.7;
    transition: opacity 0.2s;
  }

  .zoom-reset:hover {
    opacity: 1;
    color: #fff;
  }
`;

export default NutritionGraph;
